import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { quizService } from "../../services/api";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Button,
  Paper,
  LinearProgress,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: 8,
}));
const LoadingContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "50vh",
  "& > p": {
    marginTop: theme.spacing(2),
  },
}));

const QuizResult = () => {
  const { quizId, courseId = 0, ccrId = 0 } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [endTime, setEndTime] = useState(null);
  const [attempted, setAttempted] = useState(0);
  const [assessment, setAssessment] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [textAnswers, setTextAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userTestResult, setUserTest] = useState(null);

  useEffect(() => {
    const fetchQuizResult = async () => {
      try {
        const response = await quizService.getResultQuiz(quizId);
        const { assessment, user_test } = response.data;
        setAssessment(assessment);
        setUserTest(user_test);
        setAttempted(
          (user_test?.correct_answers || 0) + (user_test?.wrong_answers || 0)
        );
      } catch (error) {
        console.error("Failed to fetch quiz result", error);
      }
    };
    fetchQuizResult();
  }, [quizId,courseId]);

  useEffect(() => {
    if (endTime) {
      const interval = setInterval(() => {
        const timeRemaining = endTime - Date.now();
        if (timeRemaining <= 0) {
          clearInterval(interval);
          autoSubmitQuiz();
        } else {
          setTimeLeft(timeRemaining);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [endTime]);

  const submitQuiz = async () => {
    try {
      await quizService.submitQuiz({
        assessment_id: quizId,
        course_id: courseId,
        ccr_id: ccrId,
      });
      navigate(`/quiz/result/${quizId}`);
    } catch (err) {
      console.error("Failed to submit quiz", err);
    }
  };

  const autoSubmitQuiz = () => {
    submitQuiz();
  };

  const formatTime = (time) => {
    if (!time || isNaN(time) || time <= 0) return "0:00";
    const totalSeconds = Math.floor(time / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {!userTestResult ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          sx={{ backgroundColor: "#f5f5f5" }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 4,
              borderRadius: 3,
              textAlign: "center",
              backgroundColor: "#fff",
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              You haven’t attempted this quiz yet.
            </Typography>
          </Paper>
        </Box>
      ) : (
        <StyledCard>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="body1">
                {attempted} of {userTestResult.total_questions} answered
              </Typography>

              {endTime && (
                <Box display="flex" alignItems="center">
                  <AccessTimeIcon sx={{ mr: 1 }} />
                  <Typography id="quiz-timer" variant="body1">
                    {formatTime(timeLeft)}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box mb={2}>
              <LinearProgress
                variant="determinate"
                value={userTestResult.total_questions ? (attempted / userTestResult.total_questions) * 100 : 0}
                sx={{
                  height: 8,
                  borderRadius: 5,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#2563eb",
                  },
                  backgroundColor: "e0115e",
                }}
              />
            </Box>

             {userTestResult?.grade_status === 1 ? (
              <Box mb={2} display="flex" alignItems="center" gap={2}>
                <Typography variant="h6">Score:</Typography>
                <Typography variant="body1">
                  {userTestResult.total_score > 0
                    ? (
                        (userTestResult.obtained_score * 100) /
                        userTestResult.total_score
                      ).toFixed(2) + "%"
                    : "0%"}
                </Typography>
                <Typography variant="h6">Result:</Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color:
                      (userTestResult.obtained_score * 100) / userTestResult.total_score >=
                      assessment.pass_percentage
                        ? "green"
                        : "red",
                    fontWeight: "bold",
                    ml: 2,
                  }}
                >
                  {(userTestResult.obtained_score * 100) / userTestResult.total_score >=
                  assessment.pass_percentage
                    ? "Passed"
                    : "Failed"}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body1" color="warning.main" mb={2}>
                Your test is under review.
              </Typography>
            )} 

 


            {assessment?.show_ans &&
              (() => {
                let detailButton = false;
                let percent = 0;

                if (userTestResult?.obtained_score > 0) {
                  percent = (
                    (userTestResult.obtained_score * 100) /
                    userTestResult.total_score
                  ).toFixed(2);
                }

                if (
                  (percent < 40 && assessment.answers_pass_fail === 2) ||
                  percent > 40
                ) {
                  detailButton = true;
                }

                return detailButton ? (
                  <Button
                    variant="contained"
                    href={`/quiz/result-details/${assessment.id}`}
                    sx={{
                      backgroundColor: "#2563eb",
                      "&:hover": {
                        backgroundColor: "#c00d51",
                      },
                      mt: 2,
                    }}
                  >
                    View Details
                  </Button>
                ) : null;
              })()}
          </CardContent>
        </StyledCard>
      )}
    </Box>
  );
};

export default QuizResult;
